import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { usePrefs } from '../lib/prefs';
import type { NavItem, ViewKey } from '../nav/navModel';
import { LanguageSwitch } from './LanguageSwitch';
import { SideNav } from './SideNav';
import { ViewHeader } from './ViewHeader';

export interface ShellProps {
  userName: string;
  /** Rol (Gerencia/Bodega/Autorizador) o nombre del local, como en el prototipo. */
  userContext: string;
  nav: NavItem[];
  active: ViewKey;
  onSelect: (view: ViewKey) => void;
  onLogout: () => void;
  onLanguage?: (lang: 'es' | 'en') => void;
  actions?: ReactNode;
  children?: ReactNode;
}

/** Estructura del prototipo: .shell > aside.side (marca, usuario, menú, pie) + main.main. */
export function Shell({ userName, userContext, nav, active, onSelect, onLogout, onLanguage, actions, children }: ShellProps) {
  const { t } = useTranslation();
  const { theme, density, toggleTheme, toggleDensity } = usePrefs();
  const [navOpen, setNavOpen] = useState(false);
  return (
    <div className={navOpen ? 'shell nav-open' : 'shell'}>
      {/* Barra superior en pantallas pequeñas: el menú lateral pasa a ser un panel deslizable. */}
      <div className="mobilebar">
        <button type="button" className="iconbtn" id="navToggle" aria-label={t(navOpen ? 'common.closeMenu' : 'common.openMenu')} aria-expanded={navOpen} onClick={() => setNavOpen((v) => !v)}>
          <Icon name={navOpen ? 'close' : 'menu'} size={20} />
        </button>
        <b>TEO</b>
        <span className="mobileview">{t(`nav.${active}`)}</span>
      </div>
      <button type="button" className="scrim" aria-label={t('common.closeMenu')} tabIndex={navOpen ? 0 : -1} onClick={() => setNavOpen(false)} />
      <aside className="side">
        <div className="brand">
          <img src="/teo-logo.png" alt="TEO Inventory Control" />
          <div>
            <b>TEO</b>
            <small>{t('common.brandSubtitle')}</small>
          </div>
        </div>
        <div className="who" id="who">
          <b>{userName}</b>
          <br />
          {userContext}
        </div>
        <SideNav items={nav} active={active} onSelect={(v) => { setNavOpen(false); onSelect(v); }} />
        <div className="sidefoot">
          <div className="prefs">
            <LanguageSwitch onChange={onLanguage} />
            <button type="button" className="iconbtn" id="themeToggle" aria-pressed={theme === 'dark'} aria-label={t(theme === 'dark' ? 'common.themeLight' : 'common.themeDark')} title={t(theme === 'dark' ? 'common.themeLight' : 'common.themeDark')} onClick={toggleTheme}>
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} />
            </button>
            <button type="button" className="iconbtn" id="densityToggle" aria-pressed={density === 'compact'} aria-label={t(density === 'compact' ? 'common.densityComfortable' : 'common.densityCompact')} title={t(density === 'compact' ? 'common.densityComfortable' : 'common.densityCompact')} onClick={toggleDensity}>
              <Icon name={density === 'compact' ? 'rowsWide' : 'rows'} size={16} />
            </button>
          </div>
          <button type="button" className="btn" id="logout" onClick={onLogout}>
            <Icon name="logout" size={15} /> {t('common.logout')}
          </button>
        </div>
      </aside>
      <main className="main">
        <ViewHeader view={active} actions={actions} />
        <section className="view active" id={`v-${active}`}>
          {children}
        </section>
      </main>
    </div>
  );
}
